import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { first, last } from 'lodash';
import { ReactNode, useRef } from 'react';
import { Entity } from '@shared/data/entity/Entity';
import styles from './VirtualCardList.module.css';

const OVERSCAN = 5;
const ESTIMATED_CARD_HEIGHT = 120;

interface VirtualCardListProps {
  entities: Entity[];
  renderItem: (info: { item: Entity; index: number }) => ReactNode;
  ListFooterComponent?: ReactNode;
  className?: string;
  contentClassName?: string;
  testID?: string;
}

export function VirtualCardList({
  entities,
  renderItem,
  ListFooterComponent,
  className,
  contentClassName,
  testID,
}: VirtualCardListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: entities.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_CARD_HEIGHT,
    overscan: OVERSCAN,
    getItemKey: index => entities[index]?.id ?? index,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = first(virtualItems)?.start ?? 0;
  const paddingBottom = totalSize - (last(virtualItems)?.end ?? 0);

  return (
    <div
      ref={scrollRef}
      className={clsx(styles.virtualCardList, className, contentClassName)}
      data-testid={testID}
    >
      {paddingTop > 0 && <div style={{ height: paddingTop }} aria-hidden />}
      {virtualItems.map(virtualItem => {
        const entity = entities[virtualItem.index];
        return (
          <div key={entity.id} data-index={virtualItem.index} ref={virtualizer.measureElement}>
            {renderItem({ item: entity, index: virtualItem.index })}
          </div>
        );
      })}
      {paddingBottom > 0 && <div style={{ height: paddingBottom }} aria-hidden />}
      {ListFooterComponent && <div>{ListFooterComponent}</div>}
    </div>
  );
}
